import {Component, OnDestroy, OnInit} from '@angular/core';
import {TasksService} from '../../../components/tasks/tasks.service';
import {
    ConfiguratorTaskService,
    DatastoreDto,
    EngineEtlService,
    PageTaskLiteDTO,
    Partner,
    TaskDTO,
    TaskFilterDTO
} from '@tj/tj-dmp-lib-api';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {ConfirmationService} from 'primeng/api';
import {DmpWebsocketService} from '../../../services/dmp-websocket.service';
import {Subscription} from 'rxjs/internal/Subscription';
import {FormArray, FormBuilder, FormGroup} from '@angular/forms';
import {formatDate} from '@angular/common';
import { PagePermissionsService } from '../../../auth/page-permissions.service';
import {UtilsService} from '../../../services/utils.service';

@Component({
    selector: 'dmp-etl-task',
    templateUrl: './etl-task.component.html',
    styleUrls: ['./etl-task.component.scss'],
    standalone: false
})
export class EtlTaskComponent implements OnInit, OnDestroy {
    breadcrumb = [
        {label: this.translate.instant('menu.flow.label')},
        {label: this.translate.instant('menu.flow.tasks')}
    ];
    public statusOptions = [
        {value: 'IN_PROGRESS', label: this.translate.instant('etl.common.IN_PROGRESS')},
        {value: 'END', label: this.translate.instant('etl.common.END')},
        {value: 'WAIT', label: this.translate.instant('etl.common.WAIT')},
        {value: 'ERROR', label: this.translate.instant('etl.common.ERROR')},
        {value: 'INTERRUPTED', label: this.translate.instant('etl.common.INTERRUPTED')}
    ];
    public typeOptions = [{
        label: this.translate.instant('etl.common.IMPORT_DATA'),
        value: 'IMPORT_DATA',
        permission: 'IMPORTS.read'
    }, {
        label: this.translate.instant('etl.common.EXPORT_DATA'),
        value: 'EXPORT_DATA',
        permission: 'EXPORTS.read'
    }
    ];
    description = this.translate.instant('etl.task.description');
    tasks: TaskDTO[] = [];
    loading = false;

    public datastore: string;
    public forceDirtyForm = false;

    public filterForm: FormGroup;


    private subs: Array<Subscription> = [];
    public data: PageTaskLiteDTO;
    public page = 0;
    public size = 20;
    public first = 0;
    private partners: Partner[];

    constructor(
        private readonly tasksService: TasksService,
        private readonly configuratorTaskService: ConfiguratorTaskService,
        private readonly engineEtlService: EngineEtlService,
        private readonly router: Router,
        private readonly translate: TranslateService,
        private readonly confirmationService: ConfirmationService,
        private readonly dmpWebsocketService: DmpWebsocketService,
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private pagePermissionsService : PagePermissionsService,
        private readonly utilsService: UtilsService
    ) {
        this.filterForm = this.fb.group({
            status: [this.getAllStatusValues()],
            type: [this.getFilteredTypes([])],
            etlIds: [[]],
            datastore: [],
            dateCreationRange: [[]]
        });
        this.translate.onLangChange.subscribe(() => this.rebuildTranslatedHeader());
    }

    private rebuildTranslatedHeader(): void {
        this.breadcrumb = [
            {label: this.translate.instant('menu.flow.label')},
            {label: this.translate.instant('menu.flow.tasks')}
        ];
        this.description = this.translate.instant('etl.task.description');
    }

    private getAllStatusValues(): string[] {
        return this.statusOptions.map(o => o.value);
    }

    /** Une sélection vide équivaut à "pas de filtre" côté backend : on revient visuellement à "tout" à la fermeture du panneau */
    onStatusPanelHide(): void {
        if (!this.filterForm.get('status').value?.length) {
            this.filterForm.get('status').setValue(this.getAllStatusValues());
        }
    }

    onTypePanelHide(): void {
        if (!this.filterForm.get('type').value?.length) {
            this.filterForm.get('type').setValue(this.getFilteredTypes([]));
        }
    }

    ngOnInit(): void {
        this.subs.push(this.dmpWebsocketService.listenerUpdateDataTaskObserver.subscribe(res => {
            const convertedTask = {
                ...res,
                dateCreation: this.utilsService.normalizeTimestampToDate(res.dateCreation),
                dateFin: this.utilsService.normalizeTimestampToDate(res.dateFin),
                dateExec: this.utilsService.normalizeTimestampToDate(res.dateExec),
                lastExecutionEnd: this.utilsService.normalizeTimestampToDate(res.lastExecutionEnd)
            };
            const taskIndex = this.tasks.findIndex(t => t.id === res.id);
            if (taskIndex > -1) {
                this.tasks[taskIndex] = convertedTask;
            } else {
                this.tasks.unshift(convertedTask);
            }
        }));
        // this.refresh();
        this.route.queryParamMap.subscribe(params => {
            if (params.has('datastore')) {
                this.datastore = params.get('datastore');
            }
            const statusParams = params.getAll('status');
            const typeParams = params.getAll('type');
            this.filterForm.patchValue({
                status: statusParams.length ? statusParams : this.getAllStatusValues(),
                type: typeParams.length ? typeParams : this.getFilteredTypes([]),
                etlIds: params.getAll('etlIds'),
                datastore: params.get('datastore') || this.datastore,
                dateCreationRange: (params.get('dateCreationStart') && params.get('dateCreationEnd'))
                    // tslint:disable-next-line:no-non-null-assertion
                    ? [new Date(params.get('dateCreationStart')!), new Date(params.get('dateCreationEnd')!)]
                    : [],
            });

            // Appeler filter() à chaque changement de query params seulement si on a un datastore
            if (this.datastore) {
                setTimeout(() => {
                    this.filter();
                }, 100);
            }
        });
    }

    ngOnDestroy(): void {
        if (this.subs) {
            this.subs.forEach(i => i.unsubscribe());
        }
    }

    action(name: 'view' | 'edit' | 'delete', task: TaskDTO, event) {
        switch (name) {
            case 'view':
                this.router.navigate(['/h/flux/import-v1/task', task.tenant, task.id]);
                break;
            case 'delete':
                this.confirmationService.confirm({
                    target: event.target,
                    key: 'task',
                    message: this.translate.instant('common.confirm'),
                    acceptLabel: this.translate.instant('common.True'),
                    rejectLabel: this.translate.instant('common.False'),
                    icon: 'pi pi-exclamation-triangle',
                    accept: () => {
                        this.configuratorTaskService.deleteTaskUsingDELETE(task.tenant, task.id).subscribe(() => {
                            // this.refresh();
                        });
                    },
                    reject: () => {
                        // not yet
                    }
                });
                break;
        }
    }

    getActionsList(task) {
        return [
            {
                label: 'Supprimer...',
                icon: 'fas fa-trash',
                command: (event) => {
                    this.action('delete', task, event);
                }
            }
        ];
    }

    datastoreChange(datastore: DatastoreDto) {
        if (this.datastore !== datastore?.code) {
            this.datastore = datastore?.code;
            this.filterForm.get('datastore').setValue(this.datastore);
            this.forceDirtyForm = true;
            this.resetFilters();
        }
    }

    partnerChange(partners: Partner[]) {
        this.partners = partners;
        // this.refresh();
    }

    private setFormArray(controlName: string, values: string[]): void {
        const control = this.filterForm.get(controlName) as FormArray;
        control.clear();
        values.forEach(value => control.push(this.fb.control(value)));
    }

    private isAllSelected(values: string[], allValues: string[]): boolean {
        return values?.length === allValues.length && allValues.every(v => values.includes(v));
    }

    private updateUrl(): void {
        const formValue = this.filterForm.value;
        const queryParams: any = {};

        ['status', 'type', 'etlIds', 'affi'].forEach(field => {
            const isDefaultStatus = field === 'status' && this.isAllSelected(formValue.status, this.getAllStatusValues());
            const isDefaultType = field === 'type' && this.isAllSelected(formValue.type, this.getFilteredTypes([]));
            queryParams[field] = (formValue[field]?.length && !isDefaultStatus && !isDefaultType) ? formValue[field] : null;
        });

        if (formValue.dateCreationRange?.length === 2) {
            const [startDate, endDate] = formValue.dateCreationRange;
            if (startDate && endDate) {
                queryParams.dateCreationStart = formatDate(startDate, 'yyyy-MM-dd', 'en');
                queryParams.dateCreationEnd = formatDate(endDate, 'yyyy-MM-dd', 'en');
            }
        }

        this.router.navigate([], {
            relativeTo: this.route,
            queryParams,
            queryParamsHandling: 'merge',
        });
    }

    resetFilters(): void {
        this.filterForm.reset({
            status: this.getAllStatusValues(),
            type: this.getFilteredTypes([]),
            etlIds: [],
            dateCreationRange: [],
        });

        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {},
        });

        this.filter();
    }

    filter(): void {
        if (this.datastore) {
            const values = this.filterForm.value;
            const filteredTypes = this.getFilteredTypes(values.type || []);
            const filter: TaskFilterDTO = {
                etlIds: values.etlIds || [],
                status: values.status || [],
                dateStart: values.dateCreationRange?.length === 2 ? values.dateCreationRange[0] : null,
                dateEnd: values.dateCreationRange?.length === 2 ? values.dateCreationRange[1] : null,
                types: filteredTypes,
            };
            this.configuratorTaskService.findTasks(filter, this.datastore, this.page, this.size).subscribe(res => {
                this.data = {
                    ...res,
                    content: res.content?.map(task => ({
                        ...task,
                        dateCreation: this.utilsService.normalizeTimestampToDate(task.dateCreation),
                        dateFin: this.utilsService.normalizeTimestampToDate(task.dateFin),
                        dateExec: this.utilsService.normalizeTimestampToDate(task.dateExec),
                        lastExecutionEnd: this.utilsService.normalizeTimestampToDate(task.lastExecutionEnd)
                    }))
                };
                this.first = (this.data.number ?? 0) * (this.data.size ?? this.size);
            });
            if (this.filterForm.dirty || this.forceDirtyForm) {
                this.forceDirtyForm = false;
                this.updateUrl();
            }
        }
    }

    private getFilteredTypes(selectedTypes:TaskFilterDTO.TypesEnum[]): TaskFilterDTO.TypesEnum[]{
        const allowedTypes: TaskFilterDTO.TypesEnum[] = [];
        if (this.pagePermissionsService.hasPagePermission('IMPORTS', 'read', true)) {
            allowedTypes.push(TaskFilterDTO.TypesEnum.IMPORTDATA);
        }
        if (this.pagePermissionsService.hasPagePermission('EXPORTS', 'read', true)) {
         allowedTypes.push(TaskFilterDTO.TypesEnum.EXPORTDATA);
        }

        if(selectedTypes.length === 0){
            return allowedTypes
        }

        return selectedTypes.filter(type => allowedTypes.includes(type));

    }

    onPage(event: { first: number; rows: number }) {
        this.size = event.rows;
        this.page = Math.floor(event.first / event.rows);
        this.first = event.first;
        this.filter();
    }

    getTypeLabel(type: string): string {
        switch (type) {
            case 'IMPORT_DATA':
            case 'EXPORT_DATA':
                return this.translate.instant('etl.common.' + type);
            default:
                return type;
        }
    }

    getStatusMeta(status: string): { label: string; className: string } {
        switch (status) {
            case 'END':
                return {label: this.translate.instant('etl.common.END'), className: 'status-badge status-badge--success'};
            case 'IN_PROGRESS':
                return {label: this.translate.instant('etl.common.IN_PROGRESS'), className: 'status-badge status-badge--progress'};
            case 'WAIT':
                return {label: this.translate.instant('etl.common.WAIT'), className: 'status-badge status-badge--wait'};
            case 'ERROR':
                return {label: this.translate.instant('etl.common.ERROR'), className: 'status-badge status-badge--error'};
            case 'INTERRUPTED':
                return {label: this.translate.instant('etl.common.INTERRUPTED'), className: 'status-badge status-badge--interrupted'};
            default:
                return {label: status, className: 'status-badge status-badge--default'};
        }
    }
}
